import { hashKey, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  BulkEditListingClient,
  Event,
  Feature,
  Listing,
  ListingPriceCalculationsInEventInput,
} from 'src/WebApiController';

import { useDebouncedValueSafe } from '../useDebouncedValueSafe';
import { useUserHasFeature } from '../useUserHasFeature';

export const useListingGroupAutoPricePreview = (
  event: Event,
  listings: Listing[],
  rawPricingSettingsUpdates: ListingPriceCalculationsInEventInput | undefined
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { trackEndpoint } = useAppPerformanceContext();

  const [debouncedPricingSettingsUpdate] = useDebouncedValueSafe(
    rawPricingSettingsUpdates
  );

  const listingIds = useMemo(() => listings.map((l) => l.id), [listings]);
  const maxVelocityCircuitBreakerStateRowVersion = useMemo(() => {
    const rowVersions = listings
      .map((listing) => listing.cirBrSuppRowVer)
      .filter((version) => version !== null && version !== undefined);

    return rowVersions.length ? Math.max(...rowVersions) : null;
  }, [listings]);

  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
    event.viagId &&
    !isEmpty(listingIds) &&
    debouncedPricingSettingsUpdate &&
    hasAutoPricingFeature
  );

  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!debouncedPricingSettingsUpdate) {
      return undefined;
    }
    const { rankPremium, ...rest } = debouncedPricingSettingsUpdate;
    // Temp solution to get pricing calculation with most updated rank premium,
    // Pricer uses the cached group pricing settings
    // Override the hasChanged to be true to passdown whatever from UI
    const result: ListingPriceCalculationsInEventInput = {
      ...rest,
      rankPremium: !rankPremium
        ? null
        : { hasChanged: true, value: rankPremium.value },
    };

    // Only include velocity circuit breaker max row version if feature is enabled
    if (hasVeloCBFeature) {
      result.velocityCircuitBreakerStateMaxRowVersion =
        maxVelocityCircuitBreakerStateRowVersion;
    }

    return result;
  }, [
    debouncedPricingSettingsUpdate,
    maxVelocityCircuitBreakerStateRowVersion,
    hasVeloCBFeature,
  ]);

  const listingCalculationsQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
      activeAccountWebClientConfig.activeAccountId,
      event.viagId,
      hashKey([listingIds, autoPricingSettingsUpdates]),
    ],
    queryFn: async () => {
      const abortController = new AbortController();
      return await trackEndpoint(
        'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
        new BulkEditListingClient(
          activeAccountWebClientConfig
        ).bulkGetListingPriceCalculationsInEvent(
          event.viagId!,
          {
            item1: listingIds,
            item2: autoPricingSettingsUpdates!,
          },
          abortController.signal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        }
      );
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    // The response of the price calculation is huge
    // We don't want to cache the data and eat up the heap mem as the query is per input
    gcTime: 0,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError(
          'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
              viagogoEventId: event.viagId,
              listingIds,
              autoPricingSettingsUpdates,
            },
          }
        );
      },
    },
  });

  return useMemo(
    () => ({
      listingCalculationsQuery,
      bulkListingPriceCalculationResult: listingCalculationsQuery.data,
    }),
    [listingCalculationsQuery]
  );
};
